'use client'

import { canEditClaim, getDisabledClaimTooltip } from '@repo/utils/claimPermissions'
import { convertDateToLocalTimeInIsoFormat, parseDate } from '@repo/utils/time'
import { AddIcon } from '@repo/ui/AddIcon'
import { AddressInput } from '@repo/ui/AddressInput'
import { ClaimTypeSelect } from '@repo/ui/ClaimTypeSelect'
import { CloseIcon } from '@repo/ui/CloseIcon'
import { RubbishBinIcon } from '@repo/ui/RubbishBinIcon'
import { DateSelection } from '@repo/ui/DateSelection'
import { FileAttachmentIcon } from '@repo/ui/FileAttachmentIcon'
import { MatterSelect } from '@repo/ui/MatterSelect'
import { TextEntry } from '@repo/ui/TextEntry'
import { ThirdPartyNotificationRecipientSelect } from '@repo/ui/ThirdPartyNotificationRecipientSelect'
import { useClaimsStore } from '@repo/store/useClaimsStore'
import { useExpensesStore } from '@repo/store/useExpensesStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useTheme } from '@mui/material/styles'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUICheckbox from '@mui/material/Checkbox'
import MUIDivider from '@mui/material/Divider'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIIconButton from '@mui/material/IconButton'
import MUIRadio from '@mui/material/Radio'
import MUIRadioGroup from '@mui/material/RadioGroup'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'

type Location = {
  id: string
  house: string | null
  area: string | null
  postalTown: string | null
  county: string | null
  postcode: string | null
}

type ClaimType = {
  id?: string
  expenseClaimTypeId?: string
  description: string
  label?: string
  shouldCollectMileage?: boolean
  shouldCollectLocations?: boolean
  shouldCollectJourneyType?: boolean
  value?: string
}

type FormData = {
  associatedMatter: any | null
  claimRecipientUserId: string | null
  dateIncurred: Date | null
  description: string
  expenseClaimLocationsJson: string | null
  expenseClaimTypeId: string | null
  grossAmount: number | null
  isRecoverable: boolean
  isVatApplicable: boolean
  journeyType: 'oneWay' | 'return'
  locations: Location[]
  netAmount: number | null
  ratePerMile: number
  receipt: File | null
  receiptContentType: string | null
  receiptFilename: string
  receiptFileSize: number | null
  totalMiles: number | null
  vatAmount: number | null
}

type NewExpenseClaimFormProps = {
  editingClaim?: { expenseClaimId?: string } | null
  isSubmissionVersion?: boolean
  onSaveSuccess?: () => void
}

const sectionHeadingSx = {
  textTransform: 'uppercase',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'text.secondary',
  mt: 0,
  mb: 5
} as const

const locationCardSx = {
  position: 'relative',
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: 'white',
  borderRadius: 1,
  pb: 0,
  px: 3,
  pt: 4
} as const

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const createEmptyLocation = (): Location => ({
  id: Date.now().toString(),
  house: null,
  area: null,
  postalTown: null,
  county: null,
  postcode: null
})

const buildLocationsJson = (locations: Location[]): string | null => {
  const locationsArray = locations
    .map((loc, index) => {
      const hasData = loc.house || loc.area || loc.postalTown || loc.county || loc.postcode
      if (!hasData) return null
      return {
        positionIndex: index,
        ...(loc.house ? { house: loc.house } : {}),
        ...(loc.area ? { area: loc.area } : {}),
        ...(loc.postalTown ? { postalTown: loc.postalTown } : {}),
        ...(loc.county ? { county: loc.county } : {}),
        ...(loc.postcode ? { postcode: loc.postcode } : {})
      }
    })
    .filter((loc) => loc !== null)
  if (locationsArray.length === 0) return null
  return `${JSON.stringify({ locations: locationsArray })}`
}

export const ExpenseClaimForm = ({
  editingClaim,
  isSubmissionVersion = false,
  onSaveSuccess
}: NewExpenseClaimFormProps) => {
  const theme = useTheme()
  const { closeClaimsDrawer } = useClaimsStore()
  const {
    createExpenseWithLocation,
    updateExpenseWithLocation,
    fetchExpenseDetails,
    fetchExpenseClaimLocationByExpenseClaimId
  } = useExpensesStore()
  const { claimTypes } = useLookupsStore()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [fetchedStatus, setFetchedStatus] = useState<number | null>(null)
  const [hasFetchedLocations, setHasFetchedLocations] = useState(false)
  const [recipientOptions, setRecipientOptions] = useState<any[]>([])
  // selectedRecipient value is managed by ThirdPartyNotificationRecipientSelect via setSelected prop
  const [, setSelectedRecipient] = useState<any>(null)

  const [claimType, setClaimType] = useState<ClaimType | null>(null)

  const isEditingExistingExpense = !!editingClaim?.expenseClaimId
  const isStatusEditDisabled = isEditingExistingExpense && !canEditClaim(isSubmissionVersion, fetchedStatus)

  const [formData, setFormData] = useState<FormData>({
    associatedMatter: null,
    grossAmount: null,
    netAmount: null,
    description: '',
    claimRecipientUserId: null,
    expenseClaimTypeId: null,
    dateIncurred: new Date(),
    expenseClaimLocationsJson: null,
    isRecoverable: false,
    isVatApplicable: false,
    journeyType: 'oneWay',
    locations: [createEmptyLocation()],
    ratePerMile: 0.45,
    receipt: null,
    receiptContentType: null,
    receiptFilename: '',
    receiptFileSize: null,
    totalMiles: null,
    vatAmount: null
  })

  const prevLocationsRef = useRef<Location[]>(formData.locations)
  const prevJourneyTypeRef = useRef<'oneWay' | 'return'>(formData.journeyType)
  const prevTotalMilesRef = useRef<number | null>(formData.totalMiles)
  const prevIsRecoverableRef = useRef<boolean>(formData.isRecoverable)
  const prevClaimAmountNetRef = useRef<number | null>(formData.netAmount)
  const prevVatAmountRef = useRef<number | null>(formData.vatAmount)

  const prevClaimTypeRef = useRef(claimType)
  const prevAssociatedMatterRef = useRef(formData.associatedMatter)
  const prevIsVatApplicableRef = useRef(formData.isVatApplicable)

  useEffect(() => {
    if (!editingClaim?.expenseClaimId) return

    const loadExpenseDetails = async () => {
      setIsLoading(true)
      try {
        const [response, locationsResponse] = await Promise.all([
          fetchExpenseDetails({ expenseClaimId: editingClaim.expenseClaimId }),
          fetchExpenseClaimLocationByExpenseClaimId({ expenseClaimId: editingClaim.expenseClaimId })
        ])

        if (response) {
          const claimTypeFromResponse = response.expenseClaimTypeId
            ? (claimTypes?.find((ct: any) => ct.expenseClaimTypeId === response.expenseClaimTypeId) ?? {
                id: response.expenseClaimTypeId,
                expenseClaimTypeId: response.expenseClaimTypeId,
                description: response.expenseClaimTypeDescription || ''
              })
            : null
          setFetchedStatus(response.status ?? 0)

          const associatedMatter =
            response.clientNo && response.matterNo
              ? {
                  clientNo: response.clientNo,
                  matterNo: response.matterNo,
                  clientMatterNo: `${response.clientNo}-${response.matterNo}`,
                  searchTerm: `${response.clientNo}-${response.matterNo}`
                }
              : null

          const journeyType: 'oneWay' | 'return' =
            response.journeyType === 'R' || response.journeyType === 'return' ? 'return' : 'oneWay'

          const locations =
            Array.isArray(locationsResponse) && locationsResponse.length > 0
              ? locationsResponse.map((loc: any) => ({
                  id: loc.expenseClaimLocationId || loc.id || Date.now().toString(),
                  house: loc.house || null,
                  area: loc.area || null,
                  postalTown: loc.postalTown || null,
                  county: loc.county || null,
                  postcode: loc.postcode || null
                }))
              : []

          if (locations.length > 0) setHasFetchedLocations(true)

          // Pre-seed refs so the claimType and associatedMatter effects see no
          // transition and don't overwrite the data we're about to set.
          prevClaimTypeRef.current = claimTypeFromResponse
          prevAssociatedMatterRef.current = associatedMatter
          prevIsRecoverableRef.current = response.isRecoverable ?? false

          setClaimType(claimTypeFromResponse)
          setFormData({
            associatedMatter,
            grossAmount: response.grossAmount || null,
            netAmount: response.netAmount || null,
            description: response.description || '',
            claimRecipientUserId: response.claimRecipientUserId || null,
            expenseClaimTypeId: response.expenseClaimTypeId || null,
            dateIncurred: response.incurredDate
              ? parseDate(String(response.incurredDate).slice(0, 10), 'yyyy-MM-dd')
              : response.dateIncurred
                ? parseDate(String(response.dateIncurred).slice(0, 10), 'yyyy-MM-dd')
                : new Date(),
            expenseClaimLocationsJson: buildLocationsJson(locations),
            isRecoverable: response.isRecoverable ?? false,
            isVatApplicable: response.isVatApplicable || false,
            journeyType,
            locations: locations.length > 0 ? locations : [createEmptyLocation()],
            ratePerMile: 0.45,
            receipt: null,
            receiptContentType: response.receiptContentType || null,
            receiptFilename: response.receiptFilename || '',
            receiptFileSize: response.receiptFileSize || null,
            totalMiles: response.miles || null,
            vatAmount: response.vatAmount || null
          })
        }
      } catch (error) {
        console.error('Error fetching expense details:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadExpenseDetails()
  }, [editingClaim?.expenseClaimId])

  useEffect(() => {
    const prevClaimType = prevClaimTypeRef.current
    const currentClaimType = claimType

    const wasLocationsVisible = prevClaimType?.shouldCollectLocations
    const isLocationsVisible = currentClaimType?.shouldCollectLocations

    if (wasLocationsVisible && !isLocationsVisible) {
      prevLocationsRef.current = formData.locations
      setFormData((prev) => ({ ...prev, locations: [createEmptyLocation()] }))
    } else if (!wasLocationsVisible && isLocationsVisible) {
      setFormData((prev) => ({ ...prev, locations: prevLocationsRef.current }))
    }

    const wasJourneyTypeVisible = prevClaimType?.shouldCollectJourneyType
    const isJourneyTypeVisible = currentClaimType?.shouldCollectJourneyType

    if (wasJourneyTypeVisible && !isJourneyTypeVisible) {
      prevJourneyTypeRef.current = formData.journeyType
      setFormData((prev) => ({ ...prev, journeyType: 'oneWay' }))
    } else if (!wasJourneyTypeVisible && isJourneyTypeVisible) {
      setFormData((prev) => ({ ...prev, journeyType: prevJourneyTypeRef.current }))
    }

    const wasMileageVisible = prevClaimType?.shouldCollectMileage
    const isMileageVisible = currentClaimType?.shouldCollectMileage

    if (wasMileageVisible && !isMileageVisible) {
      prevTotalMilesRef.current = formData.totalMiles
      setFormData((prev) => ({ ...prev, totalMiles: null }))
    } else if (!wasMileageVisible && isMileageVisible) {
      setFormData((prev) => ({ ...prev, totalMiles: prevTotalMilesRef.current }))
    }

    prevClaimTypeRef.current = currentClaimType
  }, [claimType])

  useEffect(() => {
    const prevMatter = prevAssociatedMatterRef.current
    const currentMatter = formData.associatedMatter

    const wasMatterSelected = !!prevMatter
    const isMatterSelected = !!currentMatter

    if (wasMatterSelected && !isMatterSelected) {
      prevIsRecoverableRef.current = formData.isRecoverable
      setFormData((prev) => ({ ...prev, isRecoverable: false }))
    } else if (!wasMatterSelected && isMatterSelected) {
      setFormData((prev) => ({ ...prev, isRecoverable: prevIsRecoverableRef.current }))
    }

    prevAssociatedMatterRef.current = currentMatter
  }, [formData.associatedMatter])

  const calculateNetAndVatAmounts = (grossAmount: number) => {
    const netAmount = Math.round((100 * grossAmount * 5) / 6) / 100 // round to 2 decimal places
    const vat = Math.round(100 * (grossAmount - netAmount)) / 100 // round to 2 decimal places. This seems overkill, but is necessary due to floating point precision issues.
    return { netAmount, vat }
  }

  useEffect(() => {
    const wasVatApplicable = prevIsVatApplicableRef.current
    const isVatApplicable = formData.isVatApplicable

    if (wasVatApplicable && !isVatApplicable) {
      prevClaimAmountNetRef.current = formData.netAmount
      prevVatAmountRef.current = formData.vatAmount
      setFormData((prev) => ({ ...prev, netAmount: null, vatAmount: null }))
    } else if (!wasVatApplicable && isVatApplicable) {
      if (prevClaimAmountNetRef.current !== null || prevVatAmountRef.current !== null) {
        setFormData((prev) => ({
          ...prev,
          netAmount: prevClaimAmountNetRef.current,
          vatAmount: prevVatAmountRef.current
        }))
      } else if (formData.grossAmount) {
        const { netAmount, vat } = calculateNetAndVatAmounts(formData.grossAmount)
        setFormData((prev) => ({ ...prev, netAmount: netAmount, vatAmount: vat }))
      }
    }

    prevIsVatApplicableRef.current = isVatApplicable
  }, [formData.isVatApplicable])

  const updateFormField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[key]
        return newErrors
      })
    }
  }

  const updateLocation = (locationId: string, addressData: Omit<Location, 'id'>) => {
    setFormData((prev) => {
      const updatedLocations = prev.locations.map((loc) => (loc.id === locationId ? { ...loc, ...addressData } : loc))
      return {
        ...prev,
        locations: updatedLocations,
        expenseClaimLocationsJson: buildLocationsJson(updatedLocations)
      }
    })
    if (errors.locations) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors.locations
        return newErrors
      })
    }
  }

  const addLocation = () => {
    setFormData((prev) => {
      const updatedLocations = [...prev.locations, createEmptyLocation()]
      return {
        ...prev,
        locations: updatedLocations,
        expenseClaimLocationsJson: buildLocationsJson(updatedLocations)
      }
    })
  }

  const removeLocation = (locationId: string) => {
    setFormData((prev) => {
      const updatedLocations = prev.locations.filter((loc) => loc.id !== locationId)
      return {
        ...prev,
        locations: updatedLocations,
        expenseClaimLocationsJson: buildLocationsJson(updatedLocations)
      }
    })
  }

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      updateFormField('receipt', file)
      updateFormField('receiptFilename', file.name)
    }
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files && files.length > 0) {
      const file = files[0]
      updateFormField('receipt', file)
      updateFormField('receiptFilename', file.name)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = () => {
    setIsDragOver(false)
  }

  const handleRemoveReceipt = () => {
    setFormData((prev) => ({
      ...prev,
      receipt: null,
      receiptFilename: '',
      receiptContentType: null,
      receiptFileSize: null
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.dateIncurred || !(formData.dateIncurred instanceof Date) || isNaN(formData.dateIncurred.getTime())) {
      newErrors.dateIncurred = 'Date incurred is required'
    }
    if (!formData.claimRecipientUserId) {
      newErrors.claimRecipientUserId = 'Claim recipient is required'
    }
    if (!claimType) {
      newErrors.claimTypeId = 'Claim type is required'
    }
    if (!formData.description.trim()) {
      newErrors.claimDescription = 'Claim description is required'
    }
    if (!formData.grossAmount) {
      newErrors.grossAmount = 'Claim amount is required'
    }
    if (formData.isVatApplicable && (formData.netAmount === null || formData.netAmount === undefined)) {
      newErrors.netAmount = 'Net amount is required'
    }
    if (formData.isVatApplicable && (formData.vatAmount === null || formData.vatAmount === undefined)) {
      newErrors.vatAmount = 'VAT amount is required'
    }
    if (
      formData.isVatApplicable &&
      formData.grossAmount != null &&
      formData.netAmount != null &&
      formData.vatAmount != null &&
      Math.round(formData.grossAmount * 100) !== Math.round((formData.netAmount + formData.vatAmount) * 100)
    ) {
      newErrors.vatMismatch =
        'The gross amount does not equal Net Amount + VAT Amount. Please check this before saving.'
      if (!newErrors.grossAmount) {
        newErrors.grossAmount = 'Gross amount'
      }
    }
    if (
      formData.isRecoverable &&
      (formData.associatedMatter?.clientNo == null || formData.associatedMatter?.matterNo == null)
    ) {
      newErrors.isRecoverable =
        'A recoverable claim must have an associated matter with both a client and matter number.'
    }
    const hasPostcodeOnlyLocation = formData.locations.some(
      (loc) => loc.postcode && !loc.house && !loc.area && !loc.postalTown && !loc.county
    )
    if (hasPostcodeOnlyLocation) {
      newErrors.locations = 'When a postcode is entered, also enter at least one of House, Area, Postal town or County.'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setIsSaving(true)

    const expensePayload = {
      clientNo: formData.associatedMatter?.clientNo || null,
      claimRecipientUserId: formData.claimRecipientUserId,
      description: formData.description,
      expenseClaimLocationsJson: formData.expenseClaimLocationsJson,
      expenseClaimTypeId: formData.expenseClaimTypeId,
      grossAmount: formData.grossAmount,
      incurredDate: formData.dateIncurred
        ? convertDateToLocalTimeInIsoFormat(formData.dateIncurred).slice(0, 10)
        : null,
      isRecoverable: formData.isRecoverable,
      isVatApplicable: formData.isVatApplicable,
      journeyType: formData.journeyType === 'return' ? 'R' : 'O',
      matterNo: formData.associatedMatter?.matterNo || null,
      miles: formData.totalMiles,
      netAmount: formData.netAmount,
      ...(formData.receipt instanceof File
        ? {
            receipt: formData.receipt,
            receiptContentType: formData.receipt.type || null,
            receiptFilename: formData.receipt.name || null,
            receiptFileSize: formData.receipt.size || null
          }
        : !formData.receipt && !formData.receiptFilename
          ? {
              receipt: null,
              receiptContentType: null,
              receiptFilename: null,
              receiptFileSize: null
            }
          : {}),
      status: fetchedStatus ?? 0,
      vatAmount: formData.vatAmount
    }

    try {
      if (isEditingExistingExpense) {
        await updateExpenseWithLocation({
          ...expensePayload,
          expenseClaimId: editingClaim?.expenseClaimId,
          isSubmitter: isSubmissionVersion
        })
      } else {
        await createExpenseWithLocation(expensePayload)
      }

      setIsSaving(false)
      if (onSaveSuccess) {
        onSaveSuccess()
      } else {
        closeClaimsDrawer()
      }
    } catch (error) {
      console.error('Error saving expense claim:', error)
      setErrors({ submit: 'Failed to save expense claim. Please try again.' })

      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    closeClaimsDrawer()
  }

  const updateVatAmounts = (grossAmount: number | null) => {
    if (grossAmount && formData.isVatApplicable) {
      const { netAmount, vat } = calculateNetAndVatAmounts(grossAmount)
      setFormData((prev) => ({
        ...prev,
        grossAmount: grossAmount,
        netAmount: netAmount,
        vatAmount: vat
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        grossAmount: grossAmount
      }))
    }
  }

  const calculateMileageAmount = (miles: number | null) => {
    if (miles && claimType?.shouldCollectMileage) {
      return Math.round(miles * formData.ratePerMile * 100) / 100
    }
    return null
  }

  useEffect(() => {
    if (claimType?.shouldCollectMileage && formData.totalMiles) {
      const grossAmount = calculateMileageAmount(formData.totalMiles)
      if (grossAmount !== formData.grossAmount) {
        updateVatAmounts(grossAmount)
      }
    }
  }, [formData.journeyType])

  if (isLoading) {
    return (
      <MUIBox
        data-testid="NewExpenseClaimForm"
        sx={{
          mx: 4,
          mb: 4,
          pb: 2,
          backgroundColor: '#F4F0E7',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <MUITypography variant="body2" color="text.secondary">
          Loading claim details...
        </MUITypography>
      </MUIBox>
    )
  }

  return (
    <MUIBox
      data-testid="NewExpenseClaimForm"
      sx={{
        mx: 4,
        mb: '1rem',
        pb: 2,
        mt: '1rem',
        backgroundColor: '#F4F0E7',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <MUIBox sx={{ flex: 1, px: 5, py: 5 }}>
        <MUITypography variant="h6" sx={sectionHeadingSx}>
          Claim Details
        </MUITypography>

        <MUIStack spacing={5}>
          <MUIStack direction="row" spacing={2}>
            <MUIBox sx={{ flex: 1 }}>
              <DateSelection
                format="d MMM yyyy"
                label="Date incurred"
                value={formData.dateIncurred}
                onChange={(date: any) => updateFormField('dateIncurred', date)}
                required
                name="dateIncurred"
                enableTypedDdMmYyyy={true}
                errors={errors}
                showAdornment={true}
              />
            </MUIBox>
            <MUIBox sx={{ flex: 1 }}>
              <ThirdPartyNotificationRecipientSelect
                label="Claim recipient"
                items={recipientOptions}
                setOptions={setRecipientOptions}
                setSelected={setSelectedRecipient}
                value={formData.claimRecipientUserId ?? ''}
                onChange={(value: any) => {
                  if (value) {
                    setSelectedRecipient(value)
                    updateFormField('claimRecipientUserId', value.value)
                  } else {
                    setSelectedRecipient(null)
                    updateFormField('claimRecipientUserId', null)
                  }
                }}
                required
              />
            </MUIBox>
          </MUIStack>

          <MatterSelect
            label="Associated matter"
            name="associatedMatter"
            value={formData.associatedMatter}
            onChange={(val: any) => {
              updateFormField('associatedMatter', val)
              if (errors.isRecoverable) {
                setErrors((prev) => {
                  const newErrors = { ...prev }
                  delete newErrors.isRecoverable
                  return newErrors
                })
              }
            }}
            errors={errors}
            menuZindex={999999}
          />

          <ClaimTypeSelect
            value={claimType}
            onChange={(val: any) => {
              setClaimType(val)
              setFormData((prev) => ({
                ...prev,
                expenseClaimTypeId: val?.expenseClaimTypeId || null
              }))
              if (errors.claimTypeId) {
                setErrors((prev) => {
                  const newErrors = { ...prev }
                  delete newErrors.claimTypeId
                  return newErrors
                })
              }
            }}
            required
            errors={errors}
          />
        </MUIStack>

        {(hasFetchedLocations || claimType?.shouldCollectLocations) && (
          <>
            <MUIDivider sx={{ my: 5 }} />

            <MUITypography variant="h6" sx={sectionHeadingSx}>
              Locations
            </MUITypography>

            {errors.locations && (
              <MUITypography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0, mb: 3 }}>
                {errors.locations}
              </MUITypography>
            )}

            <MUIStack spacing={5}>
              {formData.locations.map((location) => (
                <MUIBox key={location.id} sx={locationCardSx}>
                  {formData.locations.length > 1 && (
                    <MUIIconButton
                      onClick={() => removeLocation(location.id)}
                      sx={{
                        position: 'absolute',
                        top: -12,
                        right: -12,
                        backgroundColor: 'white',
                        width: 24,
                        height: 24,
                        padding: '7px',
                        border: `1px solid lightgrey`,
                        // @ts-ignore
                        borderColor: theme.palette.border,
                        '&:hover': { backgroundColor: 'white' }
                      }}
                    >
                      <CloseIcon sx={{ width: 10, height: 10, backgroundColor: 'white' }} />
                    </MUIIconButton>
                  )}

                  <AddressInput
                    defaultValue={{
                      house: location.house,
                      area: location.area,
                      postalTown: location.postalTown,
                      county: location.county,
                      postcode: location.postcode
                    }}
                    fieldSize="small"
                    onChange={(addressData: Omit<Location, 'id'>) => updateLocation(location.id, addressData)}
                    searchWidth="100%"
                    hideExtraDiv
                  />
                </MUIBox>
              ))}

              <MUIButton
                variant="text"
                startIcon={
                  <AddIcon
                    sx={{
                      width: 20,
                      height: 20,
                      color: theme.palette.secondary.main
                    }}
                  />
                }
                onClick={addLocation}
                sx={{
                  color: theme.palette.secondary.main,
                  textTransform: 'none',
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  pl: 1,
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: theme.palette.secondary.dark
                  }
                }}
              >
                Add another location
              </MUIButton>
            </MUIStack>
          </>
        )}

        {(claimType?.shouldCollectJourneyType || claimType?.shouldCollectMileage) && (
          <>
            <MUIDivider sx={{ my: 5 }} />
            <MUITypography variant="h6" sx={sectionHeadingSx}>
              Journey Details
            </MUITypography>

            <MUIStack spacing={5}>
              {claimType?.shouldCollectJourneyType && (
                <MUIRadioGroup
                  value={formData.journeyType}
                  onChange={(e) => updateFormField('journeyType', e.target.value as 'oneWay' | 'return')}
                  row
                >
                  <MUIFormControlLabel value="oneWay" control={<MUIRadio />} label="One way" />
                  <MUIFormControlLabel value="return" control={<MUIRadio />} label="Return" />
                </MUIRadioGroup>
              )}

              {claimType?.shouldCollectMileage && (
                <MUIBox sx={{ width: '50%' }}>
                  <TextEntry
                    label="Total miles"
                    value={formData.totalMiles}
                    onChange={(val: any) => {
                      updateFormField('totalMiles', val)
                      if (claimType?.shouldCollectMileage) {
                        const grossAmount = calculateMileageAmount(val)
                        updateVatAmounts(grossAmount)
                      }
                    }}
                    isNumber
                    name="totalMiles"
                    min={0}
                    sx={{ backgroundColor: 'white' }}
                  />
                </MUIBox>
              )}
            </MUIStack>
          </>
        )}

        <MUIDivider sx={{ my: 5 }} />

        <MUITypography variant="h6" sx={{ ...sectionHeadingSx, mb: 1 }}>
          Claim Value
        </MUITypography>

        <MUITypography variant="body2" color="text.secondary" sx={{ mb: 3, fontStyle: 'italic' }}>
          *Note, you should mark your claim as VAT-applicable if you have a VAT receipt
        </MUITypography>

        <MUIStack spacing={5}>
          <MUIStack direction="row" spacing={2}>
            <MUIFormControlLabel
              control={
                <MUICheckbox
                  checked={formData.isVatApplicable}
                  onChange={(e) => updateFormField('isVatApplicable', e.target.checked)}
                />
              }
              label="VAT-applicable"
            />
            {formData.associatedMatter && (
              <MUIFormControlLabel
                control={
                  <MUICheckbox
                    checked={formData.isRecoverable}
                    onChange={(e) => updateFormField('isRecoverable', e.target.checked)}
                  />
                }
                label="Recoverable"
              />
            )}
          </MUIStack>
          {errors.isRecoverable && (
            <MUITypography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.75rem', margin: '4px 0 0' }}>
              {errors.isRecoverable}
            </MUITypography>
          )}

          {claimType?.shouldCollectMileage && (
            <MUIBox sx={{ width: '220px' }}>
              <TextEntry
                label="Rate per mile"
                value={formData.ratePerMile}
                onChange={() => {}}
                isNumber
                decimalPlaces={2}
                name="ratePerMile"
                readOnly
                sx={{ backgroundColor: 'white', width: '100%', maxWidth: '220px' }}
              />
            </MUIBox>
          )}

          <MUIStack direction="row" spacing={2}>
            <MUIBox sx={{ flex: 1 }}>
              <TextEntry
                label={formData.isVatApplicable ? 'Gross amount' : 'Amount'}
                value={formData.grossAmount}
                onChange={(val: any) => {
                  updateVatAmounts(val)
                }}
                isNumber
                decimalPlaces={2}
                name="grossAmount"
                adornment="£"
                required
                errors={errors}
                sx={{ backgroundColor: 'white', minWidth: '120px', maxWidth: '220px' }}
              />
            </MUIBox>

            {formData.isVatApplicable && (
              <MUIBox sx={{ flex: 1 }}>
                <TextEntry
                  label="Net amount"
                  value={formData.netAmount}
                  onChange={(val: any) => updateFormField('netAmount', val)}
                  isNumber
                  decimalPlaces={2}
                  name="netAmount"
                  adornment="£"
                  required
                  errors={errors}
                  sx={{ backgroundColor: 'white', minWidth: '120px' }}
                />
              </MUIBox>
            )}

            {formData.isVatApplicable && (
              <MUIBox sx={{ flex: 1 }}>
                <TextEntry
                  label="VAT amount"
                  value={formData.vatAmount}
                  onChange={(val: any) => updateFormField('vatAmount', val)}
                  isNumber
                  decimalPlaces={2}
                  name="vatAmount"
                  adornment="£"
                  required
                  errors={errors}
                  sx={{ backgroundColor: 'white', minWidth: '120px' }}
                />
              </MUIBox>
            )}
          </MUIStack>
          {errors.vatMismatch && (
            <MUITypography variant="body2" sx={{ color: '#d32f2f', fontSize: '0.75rem', margin: '4px 0 0' }}>
              The <strong>Gross Amount</strong> does not equal <strong>Net Amount</strong> + <strong>VAT Amount</strong>
              . Please check this before saving.
            </MUITypography>
          )}
        </MUIStack>

        <MUIDivider sx={{ my: 5 }} />

        <MUITypography variant="h6" sx={sectionHeadingSx}>
          Supporting Details
        </MUITypography>

        <MUIStack spacing={5}>
          <TextEntry
            label="Claim description"
            value={formData.description}
            onChange={(val: any) => updateFormField('description', val)}
            multiline
            rows={6}
            name="claimDescription"
            errors={errors}
            required
            limit={1000}
            sx={{ backgroundColor: 'white' }}
          />

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,.pdf"
            style={{ display: 'none' }}
          />

          {formData.receiptFilename ? (
            <MUIStack
              direction="row"
              alignItems="center"
              sx={{
                backgroundColor: '#F8F9FB',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                p: 1.5,
                gap: 2
              }}
            >
              <FileAttachmentIcon sx={{ height: '24px', width: '24px', mx: '4px' }} />
              <MUIStack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                <MUITypography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {formData.receiptFilename}
                </MUITypography>
                <MUITypography variant="caption" color="text.secondary">
                  {formData.receipt?.size
                    ? formatFileSize(formData.receipt.size)
                    : formData.receiptFileSize
                      ? formatFileSize(formData.receiptFileSize)
                      : ''}
                </MUITypography>
              </MUIStack>
              <MUIIconButton
                onClick={handleRemoveReceipt}
                size="small"
                sx={{ color: 'text.secondary', flexShrink: 0, '&:hover': { color: 'error.main' } }}
              >
                <RubbishBinIcon sx={{ width: 20, height: 20 }} />
              </MUIIconButton>
            </MUIStack>
          ) : (
            <MUIBox
              onDrop={handleFileDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: '2px dashed',
                borderColor: isDragOver ? theme.palette.primary.main : 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragOver ? 'rgba(80, 115, 195, 0.04)' : '#F8F9FB',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: 'rgba(80, 115, 195, 0.04)'
                }
              }}
            >
              <MUIBox
                sx={{
                  width: 48,
                  height: 48,
                  margin: '0 auto 16px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(80, 115, 195, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 16V8M12 8L8 12M12 8L16 12"
                    stroke="#5073C3"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15"
                    stroke="#5073C3"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </MUIBox>
              <MUITypography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Upload receipt
              </MUITypography>
              <MUITypography variant="body2" color="text.secondary">
                Drag-and-drop or{' '}
                <MUITypography
                  component="span"
                  variant="body2"
                  sx={{ color: theme.palette.primary.main, textDecoration: 'underline' }}
                >
                  click here
                </MUITypography>{' '}
                to upload a receipt
              </MUITypography>
            </MUIBox>
          )}
        </MUIStack>

        <MUIStack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 4 }}>
          <MUIButton
            variant="text"
            onClick={handleCancel}
            sx={{
              textTransform: 'uppercase',
              '&:hover': {
                backgroundColor: 'transparent'
              }
            }}
          >
            Cancel
          </MUIButton>
          <MUITooltip arrow placement="top" title={isStatusEditDisabled ? getDisabledClaimTooltip('edit') : ''}>
            <span>
              <MUIButton
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || isStatusEditDisabled}
                sx={{
                  textTransform: 'uppercase',
                  backgroundColor: theme.palette.success.light,
                  '&:hover': {
                    // @ts-ignore
                    backgroundColor: theme.palette.success.lightHover
                  },
                  '&:disabled': {
                    backgroundColor: '#B0BEC5'
                  }
                }}
              >
                {isSaving ? 'Saving...' : isEditingExistingExpense ? 'Save' : 'Save draft'}
              </MUIButton>
            </span>
          </MUITooltip>
        </MUIStack>
      </MUIBox>
    </MUIBox>
  )
}
